import { Controller } from "@hotwired/stimulus"

declare const Stripe: any

export default class extends Controller {
  static targets = ["form", "submitButton", "errorContainer", "paymentMethodId"]
  static values = { publicKey: String, amount: String, clientSecret: String }

  declare readonly formTarget: HTMLFormElement
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly errorContainerTarget: HTMLElement
  declare readonly paymentMethodIdTarget: HTMLInputElement
  declare readonly publicKeyValue: string
  declare readonly amountValue: string
  declare readonly clientSecretValue: string

  private stripe: any
  private elements: any
  private paymentElement: any

  connect() {
    this.initializeStripe()
  }

  initializeStripe() {
    if (typeof Stripe === "undefined") {
      setTimeout(() => this.initializeStripe(), 100)
      return
    }

    this.stripe = Stripe(this.publicKeyValue)
    
    if (this.clientSecretValue) {
      this.handleConfirmation()
      return
    }

    this.elements = this.stripe.elements({
      mode: "payment",
      amount: Math.round(parseFloat(this.amountValue) * 100),
      currency: "mxn",
      paymentMethodCreation: "manual",
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#6D28D9',
        },
      }
    })

    this.paymentElement = this.elements.create("payment")
    this.paymentElement.mount("#payment-element")

    this.formTarget.addEventListener("submit", (e) => this.handleSubmit(e))
  }

  async handleSubmit(event: Event) {
    event.preventDefault()
    this.setLoading(true)

    if (!this.elements) {
      this.showError("Payment form is not ready yet")
      this.setLoading(false)
      return
    }

    // Required by Stripe Payment Element before confirming/creating PMs
    const { error: submitError } = await this.elements.submit()
    if (submitError) {
      this.showError(submitError.message || "Please review your payment details")
      this.setLoading(false)
      return
    }

    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      elements: this.elements,
    })

    if (error) {
      this.showError(error.message || "An error occurred")
      this.setLoading(false)
      return
    }

    // Pass PaymentMethod ID to the backend
    if (this.hasPaymentMethodIdTarget) {
      this.paymentMethodIdTarget.value = paymentMethod.id
    }
    
    this.formTarget.submit()
  }

  async handleConfirmation() {
    this.setLoading(true)
    const { error } = await this.stripe.confirmPayment({
      clientSecret: this.clientSecretValue,
      confirmParams: {
        return_url: window.location.href,
      },
    })

    if (error) {
      this.showError(error.message || "Confirmation failed")
      this.setLoading(false)
    }
  }

  private showError(message: string) {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = message
      this.errorContainerTarget.classList.remove("hidden")
    } else {
      alert(message)
    }
  }

  private setLoading(isLoading: boolean) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = isLoading
      if (isLoading) {
        this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
      } else {
        this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
      }
    }
  }
}
